/* eslint-disable no-console */
import { ENVIRONMENT } from '../config/environment';
import { isEmptyArray } from './is_empty_array';
import { captureException, addBreadcrumb } from './observability';

const LogLevels = {
  info: 'info',
  error: 'error',
  warning: 'warning',
  debug: 'debug',
} as const;

interface LogFunctionParams {
  message: string;
  extra?: Record<string, unknown>;
}

interface ErrorLogFunctionParams extends LogFunctionParams {
  error: Error;
}

class Logger {
  private readonly name = 'poppulo-feeds-workplace-loader';
  private static instance: Logger;

  private constructor() {
    // Do nothing.
    this.debug({
      message: 'Logger initialized',
    });
  }

  private getConsoleArguments(
    message: string,
    extra: Record<string, unknown>
  ): Array<string | Record<string, unknown>> {
    let stringSubstitutionsTemplate = '[%s] %s';
    const formattedMessage: Array<string | Record<string, unknown>> = [
      this.name,
      message.toString(),
    ];
    if (!isEmptyArray(Object.keys(extra))) {
      stringSubstitutionsTemplate += ' %o';
      formattedMessage.push(extra);
    }
    return [stringSubstitutionsTemplate, ...formattedMessage];
  }

  public static getInstance(): Logger {
    if (typeof Logger.instance === 'undefined') {
      Logger.instance = new Logger();
    }
    return Logger.instance;
  }

  private logToConsole(
    level: keyof typeof LogLevels,
    message: string,
    extra: Record<string, unknown> = {}
  ): void {
    const consoleArgs = this.getConsoleArguments(message, extra);
    switch (level) {
      case LogLevels.debug:
        console.debug(...consoleArgs);
        break;
      case LogLevels.error:
        console.error(...consoleArgs);
        break;
      case LogLevels.warning:
        console.warn(...consoleArgs);
        break;
      case LogLevels.info:
      default:
        console.info(...consoleArgs);
        break;
    }
  }

  debug({ message, extra }: LogFunctionParams): void {
    if (ENVIRONMENT.ENABLE_DEBUG) {
      this.logToConsole(LogLevels.debug, message, extra);
      addBreadcrumb({
        message,
        level: LogLevels.debug,
        data: extra,
      });
    }
  }

  info({ message, extra }: LogFunctionParams): void {
    this.logToConsole(LogLevels.info, message, extra);
    addBreadcrumb({
      message,
      level: LogLevels.info,
      data: extra,
    });
  }

  error({ message, error, extra }: ErrorLogFunctionParams): void {
    this.logToConsole(LogLevels.error, message, extra);
    captureException(error, { level: LogLevels.error, extra });
  }

  warn({ message, extra }: LogFunctionParams): void {
    this.logToConsole(LogLevels.warning, message, extra);
    addBreadcrumb({
      message,
      level: LogLevels.warning,
      data: extra,
    });
  }
}

export const logger = Logger.getInstance();
